import React, { memo, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { TourCanvasElement, TourElement, TourStepProps } from '../tour.typings'
import { TourContext } from '../tourContext'
import TourCanvas from '../tourCanvas/tourCanvas'
import TourTexts from '../tourTexts/tourTexts'
import { useDispatch, useSelector } from 'react-redux'
import { selectCurrentStep, selectTourSteps } from '../../../selectors/tour'
import { closeTour, gotoNextStep } from '../../../reducer/tour.reducer'

const TourStep: React.FC<TourStepProps> = ({ elements }) => {
    const [canvasElements, setCanvasElements] = useState<TourCanvasElement[]>([])
    const tourContext = useContext(TourContext)
    const mounted = useRef(false)
    const previousElements = useRef<TourElement[]>([])
    const previousStyles = useRef<Record<string, Record<string, any>>>({})
    const skippedSteps = useRef(new Set())
    const steps = useSelector(selectTourSteps)
    const currentStepNumber = useSelector(selectCurrentStep)
    const dispatch = useDispatch()
    const stepRef = useRef({ currentStepNumber, stepsLength: steps.length })
    stepRef.current = { currentStepNumber, stepsLength: steps.length }

    const createCanvasElements = useCallback((els: TourElement[]) => {
        if (!els || !els.length) return
        const canvasEls: TourCanvasElement[] = []
        const { currentStepNumber: step, stepsLength } = stepRef.current

        els.forEach((el) => {
            const { selector } = el
            if (skippedSteps.current.has(selector)) return
            const node = document.querySelector(selector)
            if (!node) {
                skippedSteps.current.add(selector)
                if (step < stepsLength - 1) {
                    dispatch(gotoNextStep())
                } else {
                    dispatch(closeTour())
                }
                return
            }

            const bounds = node.getBoundingClientRect()
            canvasEls.push({
                ...el,
                coordinates: {
                    topLeft: { x: bounds.left, y: bounds.top },
                    topRight: { x: bounds.right, y: bounds.top },
                    bottomLeft: { x: bounds.left, y: bounds.bottom },
                    bottomRight: { x: bounds.right, y: bounds.bottom }
                }
            })
        })

        setCanvasElements(canvasEls)
    }, [dispatch])

    const onElementsChange = useCallback((els: TourElement[], delay?: number) => {
        let totalDelay = delay || 0
        if (previousElements.current.map(v => v.selector).sort().join('') !== els.map(v => v.selector).sort().join('')) {
            previousElements.current.forEach((te) => {
                if (te.onExit) {
                    const { selector, value } = te.onExit
                    const el = document.querySelector<HTMLElement>(selector)
                    if (el) {
                        if (te.onExit.type === 'click') {
                            el.click()
                        } else if (!te.onEnter) {
                            for (let key in value) {
                                const currentValue = el.style.getPropertyValue(key)
                                if (!previousStyles.current[selector]) previousStyles.current[selector] = {}
                                if (currentValue) previousStyles.current[selector][key] = currentValue
                                el.style.setProperty(key, value[key])
                            }
                        }
                    }
                    if (!el) return
                } else if (te.onEnter && te.onEnter.type === 'style') {
                    const { selector, value } = te.onEnter
                    const el = document.querySelector<HTMLElement>(selector)
                    if (el) {
                        for (let key in value) {
                            const prevStyle = previousStyles.current[selector]
                            el.style.setProperty(key, prevStyle[key] || 'unset')
                        }
                    }
                }
            })
            previousStyles.current = {}
            els.forEach((te) => {
                totalDelay = Math.max(totalDelay, te.delay || 0)
                if (te.onEnter) {
                    const { selector, value } = te.onEnter
                    const el = document.querySelector<HTMLElement>(selector)
                    if (el) {
                        if (te.onEnter.type === 'click') {
                            el.click()
                        } else {
                            for (let key in value) {
                                const currentValue = el.style.getPropertyValue(key)
                                if (!previousStyles.current[selector]) previousStyles.current[selector] = {}
                                if (currentValue) previousStyles.current[selector][key] = currentValue
                                el.style.setProperty(key, value[key])
                            }
                        }
                    }
                    if (!el) return
                }
            })
            previousElements.current = els
        }
        setTimeout(() => createCanvasElements(els), totalDelay)
    }, [])

    useEffect(() => {
        if (!mounted.current && elements) {
            createCanvasElements(elements)
            mounted.current = true
        }
    }, [elements, createCanvasElements])

    useEffect(() => {
        if (mounted.current && elements) {
            onElementsChange(elements)
        }
    }, [elements, tourContext, onElementsChange])

    return (
        <>
            <TourCanvas elements={canvasElements} />
            <TourTexts elements={canvasElements} />
        </>
    )
}

export default memo(TourStep)